import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus } from "lucide-react";

export default function Team() {
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState({ email: "", name: "", role: "AGENT", password: "" });
    const [err, setErr] = useState("");

    const load = () => api.get("/users").then((r) => setRows(r.data));
    useEffect(() => {
        load();
    }, []);

    const add = async (e) => {
        e.preventDefault();
        setErr("");
        try {
            await api.post("/users", form);
            setForm({ email: "", name: "", role: "AGENT", password: "" });
            load();
        } catch (ex) {
            setErr(formatApiError(ex));
        }
    };

    const toggle = async (u) => {
        await api.patch(`/users/${u.id}`, { disabled: !u.disabled });
        load();
    };
    const setRole = async (u, role) => {
        await api.patch(`/users/${u.id}`, { role });
        load();
    };

    return (
        <div className="space-y-4 max-w-4xl" data-testid="team-page">
            <h1 className="text-2xl font-bold tracking-tight">Team</h1>
            <form
                onSubmit={add}
                className="grid grid-cols-1 md:grid-cols-5 gap-2 bg-card border border-border rounded-md p-3 items-end"
            >
                <Field label="Email" testId="team-email-input">
                    <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="bg-background border border-border rounded-md px-3 py-2 text-sm w-full focus:outline-none focus:border-primary"
                    />
                </Field>
                <Field label="Name" testId="team-name-input">
                    <input
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="bg-background border border-border rounded-md px-3 py-2 text-sm w-full focus:outline-none focus:border-primary"
                    />
                </Field>
                <Field label="Role" testId="team-role-select">
                    <select
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        className="bg-background border border-border rounded-md px-3 py-2 text-sm w-full"
                    >
                        <option value="AGENT">AGENT</option>
                        <option value="ADMIN">ADMIN</option>
                    </select>
                </Field>
                <Field label="Password (optional)" testId="team-password-input">
                    <input
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="bg-background border border-border rounded-md px-3 py-2 text-sm w-full focus:outline-none focus:border-primary"
                    />
                </Field>
                <button
                    data-testid="team-add-btn"
                    className="px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm inline-flex items-center justify-center gap-1"
                >
                    <Plus className="size-3.5" /> Add
                </button>
            </form>
            {err && (
                <div className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2 rounded-md">
                    {err}
                </div>
            )}
            <div className="bg-card border border-border rounded-md divide-y divide-border">
                {rows.map((u) => (
                    <div
                        key={u.id}
                        className="flex items-center gap-3 px-3 py-2 text-sm"
                        data-testid={`team-row-${u.id}`}
                    >
                        <div className="flex-1">
                            <div className="font-medium">{u.name}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                        </div>
                        <select
                            data-testid={`team-role-${u.id}`}
                            value={u.role}
                            onChange={(e) => setRole(u, e.target.value)}
                            className="bg-background border border-border rounded-md px-2 py-1 text-xs"
                        >
                            <option value="AGENT">AGENT</option>
                            <option value="ADMIN">ADMIN</option>
                        </select>
                        <button
                            data-testid={`team-toggle-${u.id}`}
                            onClick={() => toggle(u)}
                            className={`text-[10px] px-2 py-0.5 rounded-sm border uppercase tracking-wider ${
                                u.disabled
                                    ? "bg-muted text-muted-foreground border-border"
                                    : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                            }`}
                        >
                            {u.disabled ? "Disabled" : "Active"}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Field({ label, children, testId }) {
    return (
        <div data-testid={testId}>
            <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
                {label}
            </label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
